import { List, Map, fromJS } from 'immutable';
import { genKey } from 'draft-js';

import { parseJsonApi } from '../../utils/jsonapi';
import cleanUpBlocks from '../../utils/cleanUpBlocks';

import {
  SPELL_CHECK_SUCCEED,
  SPELL_CHECK_REMOVE_SPELLING,
} from '../SpellCheck/constants';
import { SETUP } from '../../export/Editor/constants';

import {
  RESET,
  CREATE_BLOCK,
  MOVE_BLOCK,
  DELETE_MEDIA,
  DELETE_BLOCK,
  DELETE_EMPTY_BLOCKS,
  CLEAN_UP_CONTENT,
  SET_VALUE,
  MERGE_BLOCK_DATA,
  SET_BLOCK_TYPE,
  SET_BLOCK_ACTIVE,
  SET_BLOCK_NOT_NEW,
} from './constants';

import {
  UPLOAD_MEDIA_REQUESTED,
  UPLOAD_MEDIA_SUCCEED,
  UPLOAD_MEDIA_FAILED,
} from '../../api/medialib/constants';

export const initialState = Map({
  allowedBlockTypes: null,
  colorScheme: null,
  data: List(),
  initialData: List(),
  readOnly: false,
  activeBlock: null,
  newBlock: null,
});

const createBlock = (type, blockObject = {}) =>
  getBlockInitialState(type).merge({ key: genKey() }, blockObject);

export const getBlockInitialState = (type) => {
  let block = Map({
    type,
    isValid: null,
    spellings: List(),
  });

  switch (type) {
    case 'TEXT':
      block = block.merge({
        value: '',
      });
      break;

    case 'COLLAPSE':
      block = block.merge({
        title: '',
        content: '',
      });
      break;

    case 'QUOTE':
      block = block.merge({
        content: '',
        cite: '',
      });
      break;

    case 'IMAGE':
      block = block.merge({
        description: '',
        uploading: false,
      });
      break;

    case 'VIDEO':
      block = block.merge({
        uploading: false,
      });
      break;

    default:
      break;
  }

  return block;
};

export default function editorReducer(state = initialState, action) {
  switch (action.type) {
    case CREATE_BLOCK:
      return state.update((prevState) => {
        const newBlock = createBlock(
          action.payload.blockType,
          action.payload.blockObject
        );
        return prevState
          .update('data', (blocks) =>
            Number.isInteger(action.payload.position)
              ? blocks.insert(action.payload.position, newBlock)
              : blocks.push(newBlock)
          )
          .set('newBlock', newBlock.get('key'))
          .set('activeBlock', newBlock.get('key'));
      });

    case MOVE_BLOCK:
      return state.update('data', (blocks) => {
        const block = blocks.get(action.payload.currentPosition);

        return blocks
          .delete(action.payload.currentPosition)
          .insert(action.payload.newPosition, block);
      });

    case DELETE_MEDIA:
      return state.update('data', (blocks) =>
        blocks.map((block) => {
          if (block.get('key') !== action.payload.key) return block;

          switch (block.get('type')) {
            case 'VIDEO':
              return block.delete('video');
            case 'IMAGE':
            case 'QUOTE':
              return block.delete('image');
            default:
              return block;
          }
        })
      );

    case DELETE_BLOCK:
      return state.update('data', (blocks) =>
        blocks.filter((block) => block.get('key') !== action.payload.key)
      );

    case DELETE_EMPTY_BLOCKS:
      return state.update('data', (blocks) =>
        blocks.filter((block) =>
          (() => {
            switch (block.get('type')) {
              case 'IMAGE':
                return !!block.get('image');
              case 'VIDEO':
                return !!block.get('video');
              case 'GALLERY':
                return !!block.get('value').size;
              default:
                return block.get('value').length;
            }
          })()
        )
      );

    case CLEAN_UP_CONTENT:
      return state
        .update('data', (data) => cleanUpBlocks(data))
        .set('forceUpdateTextBlocks', true);

    case SET_VALUE:
      return state.set(action.payload.field, action.payload.value);

    case SET_BLOCK_ACTIVE:
      return state.set('activeBlock', action.payload.key);

    case SET_BLOCK_NOT_NEW:
      return state.set('newBlock', null);

    case MERGE_BLOCK_DATA:
      return state.update('data', (blocks) =>
        blocks.map((block) =>
          block.get('key') === action.payload.key
            ? block.merge(action.payload.data)
            : block
        )
      );

    case SET_BLOCK_TYPE:
      return state.update('data', (blocks) =>
        blocks.map((block) =>
          block.get('key') === action.payload.key
            ? block
                .set('type', action.payload.blockType)
                .mergeDeep(getBlockInitialState(action.payload.blockType))
            : block
        )
      );

    case UPLOAD_MEDIA_REQUESTED:
      return state.update('data', (blocks) =>
        blocks.map((block) =>
          block.get('key') === action.payload.key
            ? block.set('uploading', true).set('errors', null)
            : block
        )
      );

    case UPLOAD_MEDIA_SUCCEED:
      return state.update('data', (blocks) =>
        blocks.map((block) => {
          if (block.get('key') !== action.payload.key) {
            return block;
          }

          const item = fromJS(
            parseJsonApi(action.payload.response, 'items')
          ).first();
          let newBlock;

          switch (block.get('type')) {
            case 'IMAGE':
            case 'QUOTE':
              newBlock = block.set('image', item);
              break;
            case 'VIDEO':
              newBlock = getBlockInitialState(block.get('type')).merge(
                block.set('video', item)
              );
              break;
            default:
              newBlock = block;
              break;
          }

          return newBlock.set('uploading', false).set('errors', null);
        })
      );

    case UPLOAD_MEDIA_FAILED:
      return state.update('data', (blocks) =>
        blocks.map((block) =>
          block.get('key') === action.payload.key
            ? block
                .set('uploading', false)
                .set('errors', fromJS(action.payload.errors).toList())
            : block
        )
      );

    case SPELL_CHECK_SUCCEED:
      return action.payload.key
        ? state.update('data', (blocks) =>
            blocks.map((block) =>
              block.get('key') === action.payload.key
                ? block.set(
                    'spellings',
                    fromJS(
                      action.payload.data.map((spelling) => ({
                        word: spelling.word,
                        suggestions: spelling.s,
                        column: spelling.col,
                        row: spelling.row,
                        wordLength: spelling.len,
                        mistakeCode: spelling.code,
                      }))
                    )
                  )
                : block
            )
          )
        : state;

    case SPELL_CHECK_REMOVE_SPELLING:
      return action.payload.key
        ? state.update('data', (blocks) =>
            blocks.map((block) =>
              block.get('key') === action.payload.key
                ? block.update('spellings', (spellings) =>
                    spellings.filterNot(
                      (spelling) => spelling.get('word') === action.payload.word
                    )
                  )
                : block
            )
          )
        : state;

    case SETUP: {
      const { content, allowedBlockTypes, ...data } = action.payload.data;

      const editorContent = content
        ? fromJS(content).map((block) =>
            createBlock(block.get('type')).merge(block)
          )
        : state.get('data');

      return (
        state
          // update config
          .map((value, key) =>
            data[key] !== undefined && data.key !== null
              ? fromJS(data[key])
              : value
          )
          .set(
            'allowedBlockTypes',
            allowedBlockTypes ? fromJS(allowedBlockTypes) : null
          )
          // populate editor with passed content
          .set('data', editorContent)
          // store passed content in separate field so it can be used for comparsion later
          .set('initialData', editorContent)
      );
    }

    case RESET:
      return state.set(
        'data',
        fromJS(action.payload.content || []).map((block) =>
          createBlock(block.get('type')).merge(block)
        )
      );

    default:
      return state;
  }
}
